import { projectsProjectAccessDecisionSchema, } from '@crest/module-projects/contracts/project-access-decision';
import { projectsProjectReferenceSnapshotSchema, } from '@crest/module-projects/contracts/project-reference';
import { platformStorageAttachedObjectReferenceSchema } from '@crest/platform-contracts/storage';
import { z } from 'zod';
import { insuranceClaimsCommandReceiptSchema, insuranceClaimsIdSchema, } from './insurance-claims-claim.contract.js';
export const insuranceClaimsClaimCoachCapability = 'insurance-claims.claim-coach.v1';
export const insuranceClaimsClaimCoachVersionsCapability = 'insurance-claims.claim-coach-versions.v1';
export const insuranceClaimsClaimCoachStepKeySchema = z.enum([
    'intake',
    'inspection',
    'estimate_review',
    'carrier_scope',
    'supplement',
    'approval',
    'production',
    'invoice',
    'mortgage_check',
    'closeout',
]);
export const insuranceClaimsClaimCoachStepStatusSchema = z.enum([
    'not_started', 'in_progress', 'blocked', 'complete', 'skipped',
]);
export const insuranceClaimsClaimCoachFactStateSchema = z.enum([
    'present', 'missing', 'unavailable', 'not_required',
]);
export const insuranceClaimsClaimCoachStepDefinitions = [
    {
        key: 'intake', order: 1, title: 'Claim Intake',
        defaultNextAction: 'Collect carrier, claim, policy, loss date, and homeowner authorization details.',
        checklist: [
            ['carrier-claim-policy-loss', 'Confirm carrier, claim number, policy number, and loss date.'],
            ['customer-authorization', 'Capture customer authorization and contractor role.'],
            ['deductible-payment-indicators', 'Collect deductible, ACV/RCV, and mortgage-company indicators if known.'],
        ],
    },
    {
        key: 'inspection', order: 2, title: 'Inspection & Evidence',
        defaultNextAction: 'Complete inspection photos, measurements, damage notes, and code/ITEL needs.',
        checklist: [
            ['damage-evidence', 'Capture roof/exterior photos and room-by-room damage evidence.'],
            ['measurements', 'Request measurement report or mark manual measurement required.'],
            ['specialty-needs', 'Identify code upgrades, hidden damage, and specialty inspections.'],
        ],
    },
    {
        key: 'estimate_review', order: 3, title: 'Estimate Review',
        defaultNextAction: 'Compare carrier scope, CREST scope, measurements, and missing line items.',
        checklist: [
            ['carrier-estimate', 'Import or attach carrier estimate, Xactimate export, or scope PDF.'],
            ['quantity-comparison', 'Compare scope quantities against field evidence and measurements.'],
            ['missing-items', 'Flag missing trades, detach/reset items, code items, and waste factors.'],
        ],
    },
    {
        key: 'carrier_scope', order: 4, title: 'Carrier Scope',
        defaultNextAction: 'Track whether the carrier scope is received, reviewed, accepted, or disputed.',
        checklist: [
            ['carrier-scope-status', 'Record carrier scope status and adjuster contact.'],
            ['customer-coverage-understanding', 'Confirm customer understands non-covered and betterment items.'],
            ['dispute-path', 'Prepare response path for disputed or incomplete scope items.'],
        ],
    },
    {
        key: 'supplement', order: 5, title: 'Supplement',
        defaultNextAction: 'Draft, submit, and track supplement requests with evidence.',
        checklist: [
            ['supplement-items', 'Create supplement items for missing or changed scope.'],
            ['supplement-evidence', 'Attach photos, measurements, estimate excerpts, and explanation notes.'],
            ['supplement-status', 'Track submitted, negotiating, approved, denied, or not-needed status.'],
        ],
    },
    {
        key: 'approval', order: 6, title: 'Approval & Authorization',
        defaultNextAction: 'Confirm customer authorization, carrier approval, and deductible documentation.',
        checklist: [
            ['signed-authorization', 'Confirm signed contract, customer authorization, and legal guardrails.'],
            ['deductible-status', 'Document deductible status without offering improper waiver language.'],
            ['approved-scope', 'Confirm approved scope, exclusions, upgrades, and customer selections.'],
        ],
    },
    {
        key: 'production', order: 7, title: 'Production',
        defaultNextAction: 'Move approved claim into production, materials, crew scheduling, and inspections.',
        checklist: [
            ['materials', 'Order materials and lock delivery/pickup plan.'],
            ['schedule', 'Schedule crew, site access, safety controls, and inspections.'],
            ['production-evidence', 'Capture production photos and change evidence daily.'],
        ],
    },
    {
        key: 'invoice', order: 8, title: 'Invoice & Completion Packet',
        defaultNextAction: 'Prepare completion invoice, certificate/photos, and depreciation request packet.',
        checklist: [
            ['final-invoice', 'Create final invoice aligned to approved and customer-authorized scope.'],
            ['completion-packet', 'Attach completion photos, certificate, permit/inspection documents, and warranties.'],
            ['depreciation-request', 'Request recoverable depreciation when conditions are met.'],
        ],
    },
    {
        key: 'mortgage_check', order: 9, title: 'Mortgage Check',
        defaultNextAction: 'Track mortgage endorsement, inspections, check release, and deposit status.',
        checklist: [
            ['mortgagee', 'Identify mortgagee and endorsement requirements.'],
            ['mortgage-process', 'Track inspection, endorsement, deposit, and blocked-payment status.'],
            ['funds-follow-up', 'Document customer follow-up dates until funds clear.'],
        ],
    },
    {
        key: 'closeout', order: 10, title: 'Claim Closeout',
        defaultNextAction: 'Reconcile payments, open receivables, warranties, and customer closeout.',
        checklist: [
            ['money-reconciliation', 'Reconcile ACV, RCV, deductible, supplements, customer extras, and write-offs.'],
            ['final-documents', 'Confirm warranties, final docs, and customer portal visibility.'],
            ['closeout-accounting', 'Close claim only when collections and open tasks are accounted for.'],
        ],
    },
];
const nullableText = (maximum) => z.string().trim().max(maximum).nullable();
const definitionByKey = new Map(insuranceClaimsClaimCoachStepDefinitions.map((definition) => [definition.key, definition]));
export const insuranceClaimsClaimCoachChecklistItemSchema = z.strictObject({
    itemKey: z.string().regex(/^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/),
    label: z.string().trim().min(1).max(500),
    done: z.boolean(),
});
export const insuranceClaimsClaimCoachStepSchema = z.strictObject({
    stepKey: insuranceClaimsClaimCoachStepKeySchema,
    stepOrder: z.number().int().min(1).max(10),
    title: z.string().trim().min(1).max(180),
    defaultNextAction: z.string().trim().min(1).max(1_000),
    status: insuranceClaimsClaimCoachStepStatusSchema,
    dueAt: z.string().datetime({ offset: true }).nullable(),
    completedAt: z.string().datetime({ offset: true }).nullable(),
    assignedMembershipId: z.string().uuid().nullable(),
    notes: nullableText(4_000),
    evidenceReferences: z.array(platformStorageAttachedObjectReferenceSchema).max(20),
    checklist: z.array(insuranceClaimsClaimCoachChecklistItemSchema).min(1).max(10),
}).superRefine((value, context) => {
    const expected = definitionByKey.get(value.stepKey);
    if (expected === undefined || expected.order !== value.stepOrder) {
        context.addIssue({ code: 'custom', path: ['stepOrder'], message: 'Claim Coach step order is fixed.' });
    }
    if (value.status === 'complete' && value.completedAt === null) {
        context.addIssue({ code: 'custom', path: ['completedAt'], message: 'Completed steps require a completion time.' });
    }
    if (value.status !== 'complete' && value.completedAt !== null) {
        context.addIssue({ code: 'custom', path: ['completedAt'], message: 'Only completed steps retain a completion time.' });
    }
    if ((value.status === 'blocked' || value.status === 'skipped') &&
        (value.notes === null || value.notes.length < 10)) {
        context.addIssue({ code: 'custom', path: ['notes'], message: 'Blocked or skipped steps require a retained explanation.' });
    }
    const actualKeys = value.checklist.map(({ itemKey }) => itemKey);
    const expectedKeys = expected?.checklist.map(([itemKey]) => itemKey) ?? [];
    if (new Set(actualKeys).size !== actualKeys.length ||
        actualKeys.length !== expectedKeys.length ||
        expectedKeys.some((itemKey) => !actualKeys.includes(itemKey))) {
        context.addIssue({ code: 'custom', path: ['checklist'], message: 'Claim Coach checklist identity is fixed per step.' });
    }
});
const insuranceClaimsClaimCoachStepsSchema = z.array(insuranceClaimsClaimCoachStepSchema)
    .length(10)
    .superRefine((steps, context) => {
    const keys = steps.map(({ stepKey }) => stepKey);
    if (new Set(keys).size !== 10 ||
        insuranceClaimsClaimCoachStepDefinitions.some(({ key }) => !keys.includes(key))) {
        context.addIssue({ code: 'custom', message: 'Claim Coach must retain each canonical step exactly once.' });
    }
});
export const insuranceClaimsClaimCoachVersionSchema = z.strictObject({
    contractVersion: z.literal(insuranceClaimsClaimCoachVersionsCapability),
    tenantId: z.string().uuid(),
    id: z.string().uuid(),
    coachId: z.string().uuid(),
    claimId: insuranceClaimsIdSchema,
    projectId: z.string().uuid(),
    revision: z.number().int().positive(),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
    reason: z.string().trim().min(10).max(500),
    actorMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    data: z.strictObject({
        projectReference: projectsProjectReferenceSnapshotSchema,
        projectAccessDecision: projectsProjectAccessDecisionSchema,
        steps: insuranceClaimsClaimCoachStepsSchema,
    }),
});
export const insuranceClaimsClaimCoachSchema = z.strictObject({
    contractVersion: z.literal(insuranceClaimsClaimCoachCapability),
    tenantId: z.string().uuid(),
    id: z.string().uuid(),
    claimId: insuranceClaimsIdSchema,
    projectId: z.string().uuid(),
    revision: z.number().int().nonnegative(),
    currentVersionId: z.string().uuid().nullable(),
    createdByMembershipId: z.string().uuid().nullable(),
    updatedByMembershipId: z.string().uuid().nullable(),
    createdAt: z.string().datetime({ offset: true }).nullable(),
    updatedAt: z.string().datetime({ offset: true }).nullable(),
    currentVersion: insuranceClaimsClaimCoachVersionSchema.nullable(),
    steps: insuranceClaimsClaimCoachStepsSchema,
});
export const insuranceClaimsClaimCoachStepUpdateSchema = z.strictObject({
    status: insuranceClaimsClaimCoachStepStatusSchema.optional(),
    dueAt: z.string().datetime({ offset: true }).nullable().optional(),
    assignedMembershipId: z.string().uuid().nullable().optional(),
    notes: nullableText(4_000).optional(),
    evidenceReferences: z.array(platformStorageAttachedObjectReferenceSchema).max(20).optional(),
    checklist: z.array(insuranceClaimsClaimCoachChecklistItemSchema).min(1).max(10).optional(),
}).refine((value) => Object.keys(value).length > 0, {
    message: 'Claim Coach step update must change at least one field.',
});
export const insuranceClaimsUpdateClaimCoachStepCommandSchema = z.strictObject({
    kind: z.literal('update-step'),
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().nonnegative(),
    stepKey: insuranceClaimsClaimCoachStepKeySchema,
    update: insuranceClaimsClaimCoachStepUpdateSchema,
    reason: z.string().trim().min(10).max(500),
});
export const insuranceClaimsClaimCoachCommandSchema = insuranceClaimsUpdateClaimCoachStepCommandSchema;
export const insuranceClaimsClaimCoachFactSchema = z.strictObject({
    stepKey: insuranceClaimsClaimCoachStepKeySchema,
    factKey: z.string().regex(/^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/),
    state: insuranceClaimsClaimCoachFactStateSchema,
    label: z.string().trim().min(1).max(300),
    detail: nullableText(1_000),
    capability: z.string().regex(/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+\.v[1-9][0-9]*$/),
});
export const insuranceClaimsClaimCoachProjectionSchema = z.strictObject({
    contractVersion: z.literal(insuranceClaimsClaimCoachCapability),
    coach: insuranceClaimsClaimCoachSchema,
    steps: z.array(z.strictObject({
        step: insuranceClaimsClaimCoachStepSchema,
        facts: z.array(insuranceClaimsClaimCoachFactSchema),
        evidenceState: insuranceClaimsClaimCoachFactStateSchema,
    })).length(10),
    summary: z.strictObject({
        totalSteps: z.literal(10),
        completedSteps: z.number().int().min(0).max(10),
        skippedSteps: z.number().int().min(0).max(10),
        blockedSteps: z.number().int().min(0).max(10),
        completionPercent: z.number().int().min(0).max(100),
        activeStepKey: insuranceClaimsClaimCoachStepKeySchema.nullable(),
    }),
});
export const insuranceClaimsClaimCoachResponseSchema = z.strictObject({
    data: insuranceClaimsClaimCoachProjectionSchema,
    command: insuranceClaimsCommandReceiptSchema.optional(),
});
export function createDefaultInsuranceClaimsClaimCoachSteps() {
    return insuranceClaimsClaimCoachStepDefinitions.map((definition) => insuranceClaimsClaimCoachStepSchema.parse({
        stepKey: definition.key,
        stepOrder: definition.order,
        title: definition.title,
        defaultNextAction: definition.defaultNextAction,
        status: 'not_started',
        dueAt: null,
        completedAt: null,
        assignedMembershipId: null,
        notes: null,
        evidenceReferences: [],
        checklist: definition.checklist.map(([itemKey, label]) => ({ itemKey, label, done: false })),
    }));
}
export function computeInsuranceClaimsClaimCoachProjection(input) {
    const steps = input.coach.steps.map((step) => {
        const facts = input.facts.filter((fact) => fact.stepKey === step.stepKey);
        const evidenceState = facts.some(({ state }) => state === 'unavailable')
            ? 'unavailable'
            : facts.some(({ state }) => state === 'missing')
                ? 'missing'
                : facts.some(({ state }) => state === 'present')
                    ? 'present'
                    : 'not_required';
        return { step, facts, evidenceState };
    });
    const completedSteps = input.coach.steps.filter(({ status }) => status === 'complete').length;
    const skippedSteps = input.coach.steps.filter(({ status }) => status === 'skipped').length;
    const blockedSteps = input.coach.steps.filter(({ status }) => status === 'blocked').length;
    const activeStep = input.coach.steps.find(({ status }) => status === 'in_progress') ??
        input.coach.steps.find(({ status }) => status === 'blocked') ??
        input.coach.steps.find(({ status }) => status !== 'complete' && status !== 'skipped') ?? null;
    return insuranceClaimsClaimCoachProjectionSchema.parse({
        contractVersion: insuranceClaimsClaimCoachCapability,
        coach: input.coach,
        steps,
        summary: {
            totalSteps: 10,
            completedSteps,
            skippedSteps,
            blockedSteps,
            completionPercent: Math.round((completedSteps / 10) * 100),
            activeStepKey: activeStep?.stepKey ?? null,
        },
    });
}
//# sourceMappingURL=insurance-claims-claim-coach.contract.js.map